var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Modal, ModalHeader, ModalBody, ModalFooter, Table } from '@ui/molecules';
import { Pagination } from '@ui/molecules/Table/components/Pagination';
import { SelectInput } from '@ui/molecules/inputs/form/SelectInput/SelectInput';
import { Button } from '@ui/atoms/Button/Button';
import FilterPanel from '@components/FilterPanel';
import FilterTypes from '@constants/FilterTypes';
import { translate } from '@utils/i18n';
import { IssueTypeEnum } from '@features/IIMReview/types';
import useEscapedIssuesDetail from '../hooks/useEscapedIssuesDetail';
import { exportEscapedIssues } from '../services/AnalyticReportsService';
const namespace = 'features.dashboards.inspection_assistant_insights_v2.escaped_modal';
function InspectionLinkCell({ item }) {
    return (React.createElement("a", { href: `/inspections/${item.inspectionId}#iim-issues`, target: "_blank", rel: "noopener noreferrer" }, item.inspection));
}
const INITIAL_FILTER_VALUES = {
    technician: [],
    building: []
};
const INITIAL_FILTER_ACTIVE = {
    technician: true,
    building: true
};
const ALL_ISSUE_TYPES_VALUE = '__all__';
const PAGE_SIZE = 10;
const ISSUE_TYPE_LABEL_MAP = {
    [IssueTypeEnum.UnansweredQuestion]: translate('issue_types.unanswered_questions', { namespace }),
    [IssueTypeEnum.MissingSignatures]: translate('issue_types.missing_signatures', { namespace }),
    [IssueTypeEnum.MissingDeficiencyNotesPhotos]: translate('issue_types.missing_deficiency_notes_photos', { namespace }),
    [IssueTypeEnum.MissingTimeEntries]: translate('issue_types.missing_time_entries', { namespace }),
    [IssueTypeEnum.SeverityDiscrepancy]: translate('issue_types.severity_discrepancy', { namespace }),
    [IssueTypeEnum.OutOfSpec]: translate('issue_types.out_of_spec', { namespace }),
    [IssueTypeEnum.AnalyzeDeficiencyNotes]: translate('issue_types.analyze_deficiency_notes', { namespace }),
    [IssueTypeEnum.DriftingValuesCheck]: translate('issue_types.drifting_values', { namespace }),
    [IssueTypeEnum.UlcReferenceClauseCheck]: translate('issue_types.ulc_reference_clause', { namespace }),
    [IssueTypeEnum.RecurringDeficiencies]: translate('issue_types.recurring_deficiencies', { namespace }),
    [IssueTypeEnum.ProximityCheck]: translate('issue_types.proximity_check', { namespace }),
    [IssueTypeEnum.TimingAudit]: translate('issue_types.timing_audit', { namespace }),
    [IssueTypeEnum.UnresolvedPriorDeficiencies]: translate('issue_types.unresolved_prior_deficiencies', { namespace }),
    [IssueTypeEnum.AssetCountChange]: translate('issue_types.asset_count_change', { namespace })
};
const STATUS_LABEL_MAP = {
    detected: translate('statuses.detected', { namespace }),
    resolved: translate('statuses.resolved', { namespace }),
    dismissed_by_tech: translate('statuses.dismissed_by_tech', { namespace }),
    dismissed_by_back_office: translate('statuses.dismissed_by_back_office', { namespace })
};
function humanizeIssueType(value) {
    var _a;
    return (_a = ISSUE_TYPE_LABEL_MAP[value]) !== null && _a !== void 0 ? _a : value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function humanizeStatus(value) {
    var _a;
    return (_a = STATUS_LABEL_MAP[value]) !== null && _a !== void 0 ? _a : value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
const ISSUE_TYPE_OPTIONS = [
    { value: ALL_ISSUE_TYPES_VALUE, label: translate('all_issue_types', { namespace }) },
    ...Object.entries(ISSUE_TYPE_LABEL_MAP).map(([value, label]) => ({ value, label }))
];
const COLUMNS = [
    {
        dataKey: 'inspection',
        label: translate('col_inspection', { namespace }),
        width: '22%',
        renderer: InspectionLinkCell
    },
    { dataKey: 'inspectionStatus', label: translate('col_inspection_status', { namespace }), width: '13%' },
    { dataKey: 'question', label: translate('col_question', { namespace }), width: '25%' },
    { dataKey: 'device', label: translate('col_device', { namespace }), width: '18%' },
    { dataKey: 'issueType', label: translate('col_issue_type', { namespace }), width: '12%' },
    { dataKey: 'issueStatus', label: translate('col_issue_status', { namespace }), width: '10%' }
];
function EscapedIssuesModal({ isOpen, onClose, filters }) {
    var _a, _b;
    const [issueType, setIssueType] = useState(ALL_ISSUE_TYPES_VALUE);
    const [currentPage, setCurrentPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);
    const [filterValues, setFilterValues] = useState(INITIAL_FILTER_VALUES);
    const [filterActive, setFilterActive] = useState(INITIAL_FILTER_ACTIVE);
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const handleSort = useCallback((dataKey, direction) => {
        setSortBy(direction ? dataKey : null);
        setSortDirection(direction);
        setCurrentPage(1);
    }, []);
    const issueTypeParam = issueType === ALL_ISSUE_TYPES_VALUE ? undefined : issueType;
    const { data: response, isLoading, isError } = useEscapedIssuesDetail(filters, issueTypeParam, isOpen, currentPage, PAGE_SIZE, filterValues.technician, filterValues.building, sortBy, sortDirection !== null && sortDirection !== void 0 ? sortDirection : undefined);
    const totalItems = (_b = (_a = response === null || response === void 0 ? void 0 : response.meta) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const tableData = useMemo(() => {
        if (!(response === null || response === void 0 ? void 0 : response.data))
            return [];
        return response.data.map((record) => ({
            id: record.id,
            inspectionId: record.inspectionId,
            inspection: record.inspection,
            inspectionStatus: record.inspectionStatus,
            question: record.question || '--',
            device: record.device || '--',
            issueType: humanizeIssueType(record.issueType),
            issueStatus: humanizeStatus(record.issueStatus)
        }));
    }, [response]);
    const handleIssueTypeChange = useCallback((val) => {
        setIssueType(val);
        setCurrentPage(1);
    }, []);
    const handleFilterChange = useCallback((field) => (value) => {
        setFilterValues((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
        setCurrentPage(1);
    }, []);
    const handleFilterToggle = useCallback((field) => () => {
        setFilterActive((prev) => (Object.assign(Object.assign({}, prev), { [field]: !prev[field] })));
    }, []);
    const handleResetFilters = useCallback(() => {
        setIssueType(ALL_ISSUE_TYPES_VALUE);
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterActive(INITIAL_FILTER_ACTIVE);
        setCurrentPage(1);
    }, []);
    const filtersList = useMemo(() => [
        {
            field: 'technician',
            locale: 'technician',
            type: FilterTypes.GenericMultiSelect,
            active: filterActive.technician,
            value: filterValues.technician,
            allowLetterAvatar: true,
            onChange: handleFilterChange('technician'),
            onToggle: handleFilterToggle('technician')
        },
        {
            field: 'building',
            locale: 'building',
            type: FilterTypes.GenericMultiSelect,
            active: filterActive.building,
            value: filterValues.building,
            onChange: handleFilterChange('building'),
            onToggle: handleFilterToggle('building')
        }
    ], [filterValues, filterActive, handleFilterChange, handleFilterToggle]);
    const handleExportCsv = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setIsExporting(true);
        try {
            const issueTypeParam = issueType === ALL_ISSUE_TYPES_VALUE ? undefined : issueType;
            yield exportEscapedIssues(filters, issueTypeParam, filterValues.technician, filterValues.building);
        }
        catch (_a) {
            toast.error(translate('export_error', { namespace }));
        }
        finally {
            setIsExporting(false);
        }
    }), [filters, issueType, filterValues.technician, filterValues.building]);
    if (!isOpen)
        return null;
    const modalTitle = translate('title', { namespace });
    const renderContent = () => {
        if (isError) {
            return React.createElement("div", { className: "ia-insights-modal__empty" }, translate('error', { namespace }));
        }
        if (isLoading) {
            return (React.createElement("div", { className: "ia-insights-modal__loading" },
                React.createElement("div", { className: "load-spinner load-32" },
                    React.createElement("svg", null,
                        React.createElement("use", { href: "/map.svg#load-spinner" })))));
        }
        if (tableData.length === 0) {
            return React.createElement("div", { className: "ia-insights-modal__empty" }, translate('empty', { namespace }));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Table, { data: tableData, columns: COLUMNS, variant: "detail", size: "full", sortable: true, sortableColumns: ['issueType', 'issueStatus'], onSort: handleSort, sortBy: sortBy, sortDirection: sortDirection, getItemId: (item) => item.id }),
            totalPages > 1 && (React.createElement(Pagination, { currentPage: currentPage, totalPages: totalPages, totalItems: totalItems, pageSize: PAGE_SIZE, onPageChange: setCurrentPage }))));
    };
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, variant: "base", ariaLabel: modalTitle, className: "ia-insights-modal__dialog" },
        React.createElement(ModalHeader, { title: modalTitle, onClose: onClose }),
        React.createElement(ModalBody, { className: "ia-insights-modal__body" },
            React.createElement("div", { className: "ia-insights-modal__header-row" },
                React.createElement("span", { className: "ia-insights-modal__section-title" }, translate('section_title', { namespace })),
                React.createElement("div", { className: "ia-insights-modal__issue-type-select" },
                    React.createElement(SelectInput, { options: ISSUE_TYPE_OPTIONS, value: issueType, onChange: handleIssueTypeChange }))),
            React.createElement(FilterPanel, { filters: filtersList, onResetFilters: handleResetFilters, toggleDisabled: true }),
            renderContent()),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { variant: "default", onClick: onClose }, translate('close', { namespace })),
            React.createElement(Button, { variant: "primary", onClick: handleExportCsv, disabled: isExporting || totalItems === 0 }, isExporting ? translate('exporting', { namespace }) : translate('export_csv', { namespace })))));
}
export default EscapedIssuesModal;
